import React, { useState } from 'react';
import { ArrowDown, ArrowUpRight, Check, Star } from 'lucide-react';

const BOOKING_URL = 'https://upwardcast.com/w/sovereign-choice';

const experienceFilters = [
  {
    id: 'sovereign-choice',
    practitioner: 'Goddess Iman',
    subjects: ['Nervous System', 'Somatic Release', 'Sovereignty'],
  },
  {
    id: 'reset-room',
    practitioner: 'Audrina Divine',
    subjects: ['Nervous System', 'Energy Release', 'Hypnotherapy'],
  },
  {
    id: 'destructive-relationship-liberation',
    practitioner: 'Dannielle Holmes',
    subjects: ['Destructive Relationships', 'Hypnosis'],
  },
];

const subjectOptions = [...new Set(experienceFilters.flatMap((experience) => experience.subjects))];
const practitionerOptions = [...new Set(experienceFilters.map((experience) => experience.practitioner))];

const takeaways = [
  'Understand what your nervous system is doing under pressure',
  'Move stored fear and tension through somatic release',
  'Leave with grounding SOS tools and a guided Sovereignty Activation',
];

const ExperiencesPage = () => {
  const [subjectFilter, setSubjectFilter] = useState('');
  const [practitionerFilter, setPractitionerFilter] = useState('');
  const filteredExperiences = experienceFilters.filter(
    (experience) =>
      (!subjectFilter || experience.subjects.includes(subjectFilter)) &&
      (!practitionerFilter || experience.practitioner === practitionerFilter),
  );
  const visibleExperienceIds = new Set(filteredExperiences.map((experience) => experience.id));
  const resetFilters = () => {
    setSubjectFilter('');
    setPractitionerFilter('');
  };

  return (
  <div className="relative min-h-screen overflow-hidden bg-[#10091a] text-[#fff8e8]">
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_78%_12%,rgba(117,65,141,0.23),transparent_40%),radial-gradient(ellipse_at_15%_48%,rgba(166,113,47,0.11),transparent_32%)]"
    />

    <section className="relative mx-auto max-w-7xl px-5 pb-16 pt-28 sm:px-8 sm:pb-24 sm:pt-36 lg:px-12">
      <div className="mb-10 flex items-center gap-3 sm:mb-14">
        <span className="h-px w-10 bg-[#e8c66e]" />
        <p className="text-[10px] font-semibold uppercase tracking-[0.34em] text-[#e8c66e] sm:text-xs">
          The Wisdom Network · Experiences
        </p>
      </div>

      <div className="grid items-end gap-8 lg:grid-cols-[1fr_auto] lg:gap-12">
        <div className="max-w-4xl">
          <p className="mb-5 max-w-xl text-sm leading-7 text-[#d8cfe0] sm:text-base">
            Sometimes the next step isn’t figuring everything out. It’s finding your way back to yourself.
          </p>
          <h1 className="font-title text-[2.65rem] font-medium leading-[1.04] tracking-[-0.035em] text-[#fff5dc] sm:text-6xl lg:text-[5.25rem]">
            A guided space to
            <br className="hidden sm:block" /> come back to your power.
          </h1>
        </div>
        <a
          href="#featured-experience"
          className="group inline-flex w-fit items-center gap-3 pb-1 text-xs font-semibold uppercase tracking-[0.17em] text-[#e8c66e] transition-colors hover:text-[#fff2c7] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#e8c66e]"
        >
          Browse experiences
          <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-1" />
        </a>
      </div>

      <div className="mt-12 h-px w-full bg-gradient-to-r from-[#e8c66e]/55 via-[#e8c66e]/15 to-transparent sm:mt-16" />
    </section>

    <section id="featured-experience" className="relative mx-auto max-w-7xl scroll-mt-24 px-5 pb-20 sm:px-8 sm:pb-28 lg:px-12">
      <div className="mb-7 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-[#e8c66e]">01 — 03 · Explore</p>
          <h2 className="mt-3 font-title text-2xl text-[#fff5dc] sm:text-3xl">Choose the space that meets you here.</h2>
        </div>
        <p className="text-xs text-[#bdb0c6]">Three confirmed experiences</p>
      </div>
      <div className="mb-5 rounded-2xl border border-[#e8c66e]/15 bg-[#1b1025]/75 p-4 sm:p-5">
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="experience-subject-filter" className="mb-2 block text-xs font-semibold text-[#e9dfed]">
              Subject
            </label>
            <select
              id="experience-subject-filter"
              value={subjectFilter}
              onChange={(event) => setSubjectFilter(event.target.value)}
              className="min-h-12 w-full rounded-lg border border-[#e8c66e]/25 bg-[#10091a] px-3 text-sm text-[#fff5dc] outline-none transition focus-visible:border-[#e8c66e] focus-visible:ring-2 focus-visible:ring-[#e8c66e]/30"
            >
              <option value="">All subjects</option>
              {subjectOptions.map((subject) => (
                <option key={subject} value={subject}>{subject}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="experience-practitioner-filter" className="mb-2 block text-xs font-semibold text-[#e9dfed]">
              Practitioner
            </label>
            <select
              id="experience-practitioner-filter"
              value={practitionerFilter}
              onChange={(event) => setPractitionerFilter(event.target.value)}
              className="min-h-12 w-full rounded-lg border border-[#e8c66e]/25 bg-[#10091a] px-3 text-sm text-[#fff5dc] outline-none transition focus-visible:border-[#e8c66e] focus-visible:ring-2 focus-visible:ring-[#e8c66e]/30"
            >
              <option value="">All practitioners</option>
              {practitionerOptions.map((practitioner) => (
                <option key={practitioner} value={practitioner}>{practitioner}</option>
              ))}
            </select>
          </div>
        </div>
        <p className="mt-4 text-xs text-[#bdb0c6]" role="status" aria-live="polite">
          Showing {filteredExperiences.length} {filteredExperiences.length === 1 ? 'experience' : 'experiences'}
        </p>
      </div>
      {visibleExperienceIds.has('sovereign-choice') && (
      <a
        href={BOOKING_URL}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Reserve your spot for The Sovereign Choice with Goddess Iman on UpwardCast, opens in a new tab"
        className="group block overflow-hidden rounded-[1.6rem] border border-[#e8c66e]/25 bg-[#1b1025] shadow-[0_28px_90px_rgba(0,0,0,0.35)] transition duration-500 hover:-translate-y-1 hover:border-[#e8c66e]/55 hover:shadow-[0_34px_100px_rgba(0,0,0,0.48)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#e8c66e]"
      >
        <div>
          <div className="relative aspect-video w-full overflow-hidden bg-[#160c20]">
            <img
              src="/sovereign-choice.webp"
              alt="The Sovereign Choice artwork: a luminous figure reclaiming their power beneath a radiant golden light"
              className="absolute inset-0 h-full w-full object-contain transition-opacity duration-500 group-hover:opacity-95"
              fetchPriority="high"
              decoding="async"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#12091a]/55 via-transparent to-[#12091a]/10 lg:bg-gradient-to-l lg:from-transparent lg:via-transparent lg:to-[#1b1025]/20" />
            <span className="absolute left-5 top-5 inline-flex items-center gap-2 rounded-full border border-white/25 bg-[#160c20]/55 px-3.5 py-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#fff5dc] backdrop-blur-md sm:left-7 sm:top-7">
              <span className="h-1.5 w-1.5 rounded-full bg-[#e8c66e]" />
              The Sovereign Choice
            </span>
          </div>

          <div className="flex flex-col justify-center px-6 py-9 sm:px-10 sm:py-12 lg:px-12 lg:py-16">
            <div className="mb-6 flex flex-wrap items-center gap-x-5 gap-y-3">
              <span className="text-[10px] font-semibold uppercase tracking-[0.24em] text-[#e8c66e]">
                01 / 03 · Live gathering
              </span>
              <span className="inline-flex items-center gap-1.5 text-xs text-[#d8cfe0]">
                <Star className="h-3.5 w-3.5 fill-[#e8c66e] text-[#e8c66e]" aria-hidden="true" />
                <span className="font-semibold text-[#fff5dc]">5.0</span>
                <span>· 2 attendee reviews</span>
              </span>
            </div>

            <h2 className="font-title text-3xl font-semibold leading-[1.12] tracking-[-0.025em] text-[#fff5dc] sm:text-4xl lg:text-[2.85rem]">
              The Sovereign Choice
              <span className="mt-3 block text-xl font-normal leading-snug tracking-normal text-[#e6d5ac] sm:text-2xl">
                Reclaim your power when everything feels out of control
              </span>
            </h2>

            <p className="mt-6 max-w-xl text-sm leading-7 text-[#d8cfe0] sm:text-base sm:leading-8">
              When the world feels overwhelming, it can be hard to trust your own ability to meet what comes next. Join Goddess Iman for a live, body-centered experience to calm your nervous system, release stored fear, and reconnect with the power that is already yours.
            </p>

            <div className="mt-7 border-l border-[#e8c66e]/50 pl-4">
              <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#e8c66e]">Guided by</p>
              <p className="mt-1 font-title text-xl text-[#fff5dc]">Goddess Iman</p>
              <p className="mt-1 text-xs text-[#bdb0c6]">Ascension Guide</p>
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-5 border-t border-white/10 pt-6">
              <span className="font-title text-3xl text-[#fff5dc]">$47</span>
              <span className="text-xs text-[#bdb0c6]">Reserve your spot for the live gathering</span>
            </div>

            <span className="mt-7 inline-flex min-h-14 w-full items-center justify-between gap-4 rounded-xl bg-[#e8c66e] px-5 text-sm font-bold text-[#20142a] transition-colors duration-300 group-hover:bg-[#f4dc99] sm:w-fit sm:min-w-[260px] sm:px-6">
              Reserve your spot
              <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </span>
            <span className="mt-3 text-[11px] text-[#9f91a9]">Opens securely on UpwardCast in a new tab</span>
          </div>
        </div>
      </a>
      )}

      {visibleExperienceIds.has('reset-room') && (
      <a
        href="https://upwardcast.com/w/reset-room"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Reserve your spot for The Reset Room on UpwardCast, opens in a new tab"
        className="group mt-5 block overflow-hidden rounded-[1.6rem] border border-[#e8c66e]/20 bg-[#1b1025] shadow-[0_20px_65px_rgba(0,0,0,0.28)] transition duration-500 hover:-translate-y-1 hover:border-[#e8c66e]/50 hover:shadow-[0_30px_80px_rgba(0,0,0,0.4)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#e8c66e]"
      >
        <div className="relative aspect-video w-full overflow-hidden bg-[#160c20]">
          <img
            src="/reset-room.webp"
            alt="The Reset Room artwork with luminous sacred geometry against a cosmic sky"
            className="absolute inset-0 h-full w-full object-contain transition-opacity duration-500 group-hover:opacity-95"
            loading="lazy"
            decoding="async"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#12091a]/55 via-transparent to-transparent lg:bg-gradient-to-r lg:from-transparent lg:to-[#1b1025]/20" />
          <span className="absolute left-5 top-5 inline-flex items-center rounded-full border border-white/25 bg-[#160c20]/55 px-3.5 py-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#fff5dc] backdrop-blur-md sm:left-7 sm:top-7">
              02 / 03
          </span>
        </div>

        <div className="flex flex-col justify-center px-6 py-8 sm:px-10 sm:py-10 lg:px-12 lg:py-12">
          <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-[#e8c66e]">
            Nervous System Reset · Energy Release · Hypnotherapy
          </p>
          <h3 className="mt-4 font-title text-3xl font-semibold leading-tight tracking-[-0.025em] text-[#fff5dc] sm:text-4xl">
            The Reset Room
          </h3>
          <p className="mt-4 text-sm text-[#d8cfe0] sm:text-base">90-Minute Virtual Intensive</p>
          <div className="mt-6 border-l border-[#e8c66e]/50 pl-4">
            <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#e8c66e]">Guided by</p>
            <p className="mt-1 font-title text-xl text-[#fff5dc]">Audrina Divine</p>
          </div>
          <span className="mt-7 inline-flex min-h-14 w-full items-center justify-between gap-4 rounded-xl border border-[#e8c66e]/45 px-5 text-sm font-bold text-[#fff5dc] transition-colors duration-300 group-hover:bg-[#e8c66e] group-hover:text-[#20142a] sm:w-fit sm:min-w-[260px] sm:px-6">
              Reserve your spot
            <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </span>
          <span className="mt-3 text-[11px] text-[#9f91a9]">Opens securely on UpwardCast in a new tab</span>
        </div>
      </a>
      )}

      {visibleExperienceIds.has('destructive-relationship-liberation') && (
      <a
        href="https://upwardcast.com/w/destructive-relationship-liberation"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Reserve your spot for A Healing & Hypnosis Journey for Destructive Relationship Liberation on UpwardCast, opens in a new tab"
        className="group mt-5 block overflow-hidden rounded-[1.6rem] border border-[#e8c66e]/20 bg-[#1b1025] shadow-[0_20px_65px_rgba(0,0,0,0.28)] transition duration-500 hover:-translate-y-1 hover:border-[#e8c66e]/50 hover:shadow-[0_30px_80px_rgba(0,0,0,0.4)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#e8c66e]"
      >
        <div className="relative aspect-video w-full overflow-hidden bg-[#160c20]">
          <img
            src="/destructive-relationship-liberation.webp"
            alt="Artwork for A Healing & Hypnosis Journey for Destructive Relationship Liberation"
            className="absolute inset-0 h-full w-full object-contain transition-opacity duration-500 group-hover:opacity-95"
            loading="lazy"
            decoding="async"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#12091a]/55 via-transparent to-transparent lg:bg-gradient-to-r lg:from-transparent lg:to-[#1b1025]/20" />
          <span className="absolute left-5 top-5 inline-flex items-center rounded-full border border-white/25 bg-[#160c20]/55 px-3.5 py-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#fff5dc] backdrop-blur-md sm:left-7 sm:top-7">
            03 / 03
          </span>
        </div>

        <div className="flex flex-col justify-center px-6 py-8 sm:px-10 sm:py-10 lg:px-12 lg:py-12">
          <h3 className="font-title text-3xl font-semibold leading-tight tracking-[-0.025em] text-[#fff5dc] sm:text-4xl">
            A Healing &amp; Hypnosis Journey for Destructive Relationship Liberation
          </h3>
          <div className="mt-6 border-l border-[#e8c66e]/50 pl-4">
            <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#e8c66e]">Guided by</p>
            <p className="mt-1 font-title text-xl text-[#fff5dc]">Dannielle Holmes</p>
          </div>
          <span className="mt-7 inline-flex min-h-14 w-full items-center justify-between gap-4 rounded-xl border border-[#e8c66e]/45 px-5 text-sm font-bold text-[#fff5dc] transition-colors duration-300 group-hover:bg-[#e8c66e] group-hover:text-[#20142a] sm:w-fit sm:min-w-[260px] sm:px-6">
            Reserve your spot
            <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </span>
          <span className="mt-3 text-[11px] text-[#9f91a9]">Opens securely on UpwardCast in a new tab</span>
        </div>
      </a>
      )}
      {filteredExperiences.length === 0 && (
        <div className="rounded-[1.6rem] border border-[#e8c66e]/20 bg-[#1b1025] px-6 py-10 text-center sm:px-10">
          <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-[#e8c66e]">No matching experiences</p>
          <h3 className="mt-3 font-title text-2xl text-[#fff5dc]">Try a different combination.</h3>
          <p className="mx-auto mt-3 max-w-md text-sm leading-6 text-[#c9bdcf]">
            There are no experiences for both of those filters. Reset them to see all three.
          </p>
          <button
            type="button"
            onClick={resetFilters}
            className="mt-6 inline-flex min-h-11 items-center justify-center rounded-lg border border-[#e8c66e]/45 px-5 text-xs font-semibold text-[#fff5dc] transition-colors hover:bg-[#e8c66e]/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#e8c66e]"
          >
            Reset filters
          </button>
        </div>
      )}
    </section>

    {visibleExperienceIds.has('sovereign-choice') && (
    <section className="relative border-y border-[#e8c66e]/15 bg-[#190f22]/75">
      <div className="mx-auto grid max-w-7xl gap-12 px-5 py-16 sm:px-8 sm:py-20 lg:grid-cols-[0.78fr_1.22fr] lg:gap-20 lg:px-12">
        <div>
          <p className="mb-5 text-[10px] font-semibold uppercase tracking-[0.3em] text-[#e8c66e]">What you’ll carry with you</p>
          <h2 className="font-title text-3xl leading-tight text-[#fff5dc] sm:text-4xl">
            A return to your own center.
          </h2>
          <p className="mt-5 max-w-md text-sm leading-7 text-[#c9bdcf]">
            This is practical, embodied support for the moments when anxiety, hypervigilance, or shutdown take over.
          </p>
        </div>
        <ul className="space-y-0">
          {takeaways.map((item, index) => (
            <li key={item} className="flex gap-5 border-b border-white/10 py-5 first:pt-0 last:border-0 last:pb-0">
              <span className="mt-0.5 font-title text-sm text-[#b59b5b]">0{index + 1}</span>
              <div className="flex gap-3">
                <Check className="mt-1 h-4 w-4 shrink-0 text-[#e8c66e]" aria-hidden="true" />
                <p className="text-sm leading-6 text-[#eee5ef] sm:text-base">{item}</p>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>
    )}

    {visibleExperienceIds.has('sovereign-choice') && (
    <section className="relative mx-auto max-w-7xl px-5 py-16 sm:px-8 sm:py-24 lg:px-12">
      <div className="grid gap-9 lg:grid-cols-[0.75fr_1.25fr] lg:items-center lg:gap-16">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-[#e8c66e]">From people who joined</p>
          <h2 className="mt-4 font-title text-3xl leading-tight text-[#fff5dc] sm:text-4xl">Real tools. A felt shift.</h2>
          <div className="mt-5 flex items-center gap-2" aria-label="Rated 5 out of 5 from 2 reviews">
            {[0, 1, 2, 3, 4].map((star) => (
              <Star key={star} className="h-4 w-4 fill-[#e8c66e] text-[#e8c66e]" aria-hidden="true" />
            ))}
            <span className="ml-2 text-xs text-[#c9bdcf]">5 from 2 reviews</span>
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <figure className="rounded-2xl border border-white/10 bg-[#1a1023] p-6 sm:p-7">
            <blockquote className="font-title text-lg leading-7 text-[#f1e7d2]">
              “The somatic release work addressed something I had been carrying in my body for quite some time. Goddess Iman guides with a quality of precision and warmth that feels rare.”
            </blockquote>
            <figcaption className="mt-6 text-xs font-semibold uppercase tracking-[0.16em] text-[#e8c66e]">Celestine</figcaption>
          </figure>
          <figure className="rounded-2xl border border-white/10 bg-[#1a1023] p-6 sm:p-7">
            <blockquote className="font-title text-lg leading-7 text-[#f1e7d2]">
              “No fluff, no filler, just real tools that land in the body immediately. I left that session standing taller.”
            </blockquote>
            <figcaption className="mt-6 text-xs font-semibold uppercase tracking-[0.16em] text-[#e8c66e]">Renata</figcaption>
          </figure>
        </div>
      </div>
    </section>
    )}

    {visibleExperienceIds.has('sovereign-choice') && (
    <section className="relative mx-auto max-w-7xl px-5 pb-20 sm:px-8 sm:pb-28 lg:px-12">
      <div className="overflow-hidden rounded-[1.5rem] border border-[#e8c66e]/25 bg-[radial-gradient(ellipse_at_85%_0%,rgba(159,111,47,0.18),transparent_42%),#21152b] px-6 py-10 sm:px-10 sm:py-12 lg:flex lg:items-center lg:justify-between lg:gap-8 lg:px-14">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-[#e8c66e]">The Sovereign Choice · Live gathering</p>
          <p className="mt-3 max-w-2xl font-title text-2xl leading-snug text-[#fff5dc] sm:text-3xl">
            External chaos doesn’t get to run the show.
          </p>
        </div>
        <a
          href={BOOKING_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-7 inline-flex min-h-14 w-full items-center justify-between gap-5 rounded-xl bg-[#e8c66e] px-5 text-sm font-bold text-[#20142a] transition duration-300 hover:-translate-y-0.5 hover:bg-[#f4dc99] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#fff5dc] sm:w-fit sm:min-w-[230px] sm:px-6 lg:mt-0"
        >
          Reserve your spot
          <ArrowUpRight className="h-5 w-5" />
        </a>
      </div>
    </section>
    )}
  </div>
  );
};

export default ExperiencesPage;
